import { useEffect, useState } from "react";
import {
    FiPackage,
    FiEdit2,
    FiPlus,
    FiStar,
    FiCheck,
    FiX,
} from "react-icons/fi";

import { useAppDispatch, useAppSelector } from "../../store/hooks";

import {
    fetchAllSmilePackages,
    createSmilePackage,
    updateSmilePackage,
} from "../../store/smileDesign/smilePackageSlice";
import TablePagination from "../../components/smileDesign/TablePagination";
import { CREDIT_SERVICE } from "../../constants/creditService";
import { getCreditUi } from "../../constants/creditPageTheme";
import {
    AdminPageHeader,
    AdminPageShell,
    AdminPrimaryButton,
} from "../../components/admin/AdminPageShell";

const PackageManager = ({
    service = CREDIT_SERVICE.SMILE,
}: {
    service?: string;
}) => {
    const dispatch = useAppDispatch();

    const { allSmilePackages, allPackagesPagination, loading } = useAppSelector(
        (state) => state.smilePackage
    );

    const [page, setPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [editingPackage, setEditingPackage] = useState<any>(null);
    const isPhotoRecolour = service === CREDIT_SERVICE.PHOTO_RECOLOUR;
    const t = getCreditUi(isPhotoRecolour ? "light" : "dark");

    const [formData, setFormData] = useState({
        package_name: "",
        price: "",
        currency: "USD",
        credits: "",
        bonus_credits: "",
        isActive: true,
    });

    const loadPackages = (nextPage = page) => {
        dispatch(fetchAllSmilePackages({ page: nextPage, service }));
    };

    useEffect(() => {
        loadPackages(1);
    }, []);

    const handleOpenCreate = () => {
        setEditingPackage(null);

        setFormData({
            package_name: "",
            price: "",
            currency: "USD",
            credits: "",
            bonus_credits: "",
            isActive: true,
        });

        setShowModal(true);
    };

    const handleOpenEdit = (pkg: any) => {
        setEditingPackage(pkg);

        setFormData({
            package_name: pkg.package_name,
            price: pkg.price,
            currency: pkg.currency,
            credits: pkg.credits,
            bonus_credits: pkg.bonus_credits,
            isActive: pkg.isActive,
        });

        setShowModal(true);
    };

    const handleSubmit = async () => {
        if (editingPackage) {
            await dispatch(
                updateSmilePackage({
                    package_id: editingPackage.package_id,
                    data: {
                        package_name: formData.package_name,
                        price: Number(formData.price),
                        currency: formData.currency,
                        credits: Number(formData.credits),
                        bonus_credits: Number(formData.bonus_credits),
                        isActive: formData.isActive,
                        service,
                    },
                })
            );
        } else {
            await dispatch(
                createSmilePackage({
                    name: formData.package_name,
                    price: Number(formData.price),
                    currency: formData.currency,
                    credits: Number(formData.credits),
                    bonus: Number(formData.bonus_credits),
                    service,
                })
            );
        }

        setShowModal(false);
        loadPackages(page);
    };

    const addButton = (
        <button
            onClick={handleOpenCreate}
            className={`flex items-center justify-center gap-2 px-6 py-3 ${t.primaryBtn}`}
        >
            <FiPlus />
            Add Package
        </button>
    );

    const content = (
        <>
            {!isPhotoRecolour && (
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 mb-8">
                    <div>
                        <h1 className={t.heading}>Package Manager</h1>
                        <p className={t.subtitle}>
                            Manage pricing plans and credit packages.
                        </p>
                    </div>
                    {addButton}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className={`${t.card} p-5`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className={`${t.muted} text-sm`}>Total Packages</p>
                            <h2 className={`text-3xl font-bold ${t.accent} mt-2`}>
                                {allPackagesPagination?.totalCount ?? 0}
                            </h2>
                        </div>
                        <div
                            className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                                isPhotoRecolour
                                    ? "border border-[#D4EBEF] bg-[#eef9fc] text-[#2fa9bf]"
                                    : "bg-cyan-500/10 border border-cyan-500/20 text-cyan-400"
                            }`}
                        >
                            <FiPackage className="text-2xl" />
                        </div>
                    </div>
                </div>

                <div className={`${t.card} p-5`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className={`${t.muted} text-sm`}>Active Packages</p>
                            <h2 className={`text-3xl font-bold ${t.success} mt-2`}>
                                {allSmilePackages.filter((pkg) => pkg.isActive).length}
                            </h2>
                        </div>
                        <div
                            className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                                isPhotoRecolour
                                    ? "bg-emerald-50 border border-emerald-200 text-emerald-600"
                                    : "bg-green-500/10 border border-green-500/20 text-green-400"
                            }`}
                        >
                            <FiStar className="text-2xl" />
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className={`text-center py-20 ${t.muted}`}>
                    Loading packages...
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {allSmilePackages.map((pkg: any) => (
                        <div
                            key={pkg.package_id}
                            className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                                pkg.isActive ? t.cardPopular : t.card
                            }`}
                        >
                            <div className="absolute top-5 right-5">
                                <div
                                    className={`flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full ${
                                        pkg.isActive
                                            ? isPhotoRecolour
                                                ? "bg-[#2fa9bf] text-white"
                                                : "bg-cyan-500 text-black"
                                            : "bg-red-500 text-white"
                                    }`}
                                >
                                    <FiStar className="text-xs" />
                                    {pkg.isActive ? "Active" : "Inactive"}
                                </div>
                            </div>

                            <div
                                className={`absolute top-0 left-0 w-full h-[4px] ${
                                    pkg.isActive
                                        ? isPhotoRecolour
                                            ? "bg-[#2fa9bf]"
                                            : "bg-cyan-400"
                                        : isPhotoRecolour
                                          ? "bg-[#E8E8E4]"
                                          : "bg-[#1f2221]"
                                }`}
                            />

                            <div className="p-8">
                                <h2 className={`text-2xl font-bold ${t.body}`}>
                                    {pkg.package_name}
                                </h2>
                                <p className={`${t.muted} mt-1`}>
                                    Total credits : {pkg.credits + pkg.bonus_credits}
                                </p>

                                <div className="mt-6 flex items-end gap-2">
                                    <h3
                                        className={`text-4xl sm:text-5xl font-extrabold ${
                                            pkg.isActive ? t.accent : t.body
                                        }`}
                                    >
                                        {pkg.currency} {pkg.price}
                                    </h3>
                                </div>

                                <div className="mt-8 space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className={t.featureIcon}>
                                            <FiCheck className="text-sm" />
                                        </div>
                                        <p className={`${t.body} text-sm`}>
                                            package Credits: {pkg.credits}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className={t.featureIcon}>
                                            <FiCheck className="text-sm" />
                                        </div>
                                        <p className={`${t.body} text-sm`}>
                                            Bonus Credits: {pkg.bonus_credits}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className={t.featureIcon}>
                                            <FiCheck className="text-sm" />
                                        </div>
                                        <p className={`${t.body} text-sm`}>
                                            Created:{" "}
                                            {new Date(pkg.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3 mt-10">
                                    <button
                                        onClick={() => handleOpenEdit(pkg)}
                                        className={`flex-1 py-3 flex items-center justify-center gap-2 ${t.primaryBtn}`}
                                    >
                                        <FiEdit2 />
                                        Edit
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <TablePagination
                pagination={allPackagesPagination}
                onPageChange={(nextPage) => {
                    setPage(nextPage);
                    loadPackages(nextPage);
                }}
                itemLabel="packages"
                variant={t.pagination}
            />

            {showModal && (
                <div className={t.modalBackdrop}>
                    <div className={`${t.modal} p-4 sm:p-6 max-h-[90vh] overflow-y-auto`}>
                        <div className="flex items-center justify-between mb-6">
                            <h2 className={`text-xl sm:text-2xl font-bold ${t.body}`}>
                                {editingPackage ? "Update Package" : "Create Package"}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className={t.iconBtn}
                            >
                                <FiX />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <input
                                type="text"
                                placeholder="Package Name"
                                value={formData.package_name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        package_name: e.target.value,
                                    })
                                }
                                className={t.input}
                            />

                            <input
                                type="number"
                                placeholder="Price"
                                value={formData.price}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        price: e.target.value,
                                    })
                                }
                                className={t.input}
                            />

                            <input
                                type="number"
                                placeholder="Credits"
                                value={formData.credits}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        credits: e.target.value,
                                    })
                                }
                                className={t.input}
                            />

                            <input
                                type="number"
                                placeholder="Bonus Credits"
                                value={formData.bonus_credits}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        bonus_credits: e.target.value,
                                    })
                                }
                                className={t.input}
                            />

                            <select
                                value={formData.currency}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        currency: e.target.value,
                                    })
                                }
                                className={t.input}
                            >
                                <option value="USD">USD</option>
                                <option value="INR">INR</option>
                            </select>

                            {editingPackage && (
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                isActive: e.target.checked,
                                            })
                                        }
                                    />
                                    <p className={`text-sm ${t.body}`}>
                                        Active Package
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={handleSubmit}
                                className={`w-full mt-4 py-3 ${t.primaryBtn}`}
                            >
                                {editingPackage ? "Update Package" : "Create Package"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    if (isPhotoRecolour) {
        return (
            <AdminPageShell>
                <AdminPageHeader
                    title="Photo Recolour Packages"
                    subtitle="Manage Photo Recolour pricing plans and credits."
                >
                    <AdminPrimaryButton
                        onClick={handleOpenCreate}
                        className="!bg-white !text-[#2fa9bf] hover:!bg-[#F8F8F6] border border-white/40"
                    >
                        <FiPlus />
                        Add Package
                    </AdminPrimaryButton>
                </AdminPageHeader>
                {content}
            </AdminPageShell>
        );
    }

    return <div className={t.page}>{content}</div>;
};

export default PackageManager;
